/**
 * Sprint 23.1 — Client-side image prep for AI Smart Scan.
 * Rotates via canvas (browsers apply EXIF automatically on decode),
 * resizes very large photos, re-exports as JPEG @ 0.85 to shave payload
 * without hurting OCR, and does quick quality checks (too small / too
 * dark) before sending to the OCR endpoint.
 */
const MAX_EDGE = 2400;                  // downscale beyond this
const MIN_EDGE = 320;                   // shorter side must be at least this
const MIN_LUMA = 40;                    // 0..255 mean luminance floor
const MAX_INPUT_BYTES = 25 * 1024 * 1024;

export const CAPTURE_ERRORS = {
  UNSUPPORTED: "unsupported",
  TOO_LARGE:   "too_large",
  TOO_SMALL:   "too_small",
  TOO_DARK:    "too_dark",
  DECODE_FAIL: "decode_fail",
};

function _loadImage(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error(CAPTURE_ERRORS.DECODE_FAIL)); };
    img.src = url;
  });
}

function _meanLuma(canvas) {
  try {
    const ctx = canvas.getContext("2d");
    // Sample a 32x32 thumbnail for speed.
    const sample = document.createElement("canvas");
    sample.width = 32; sample.height = 32;
    sample.getContext("2d").drawImage(canvas, 0, 0, 32, 32);
    const { data } = sample.getContext("2d").getImageData(0, 0, 32, 32);
    let sum = 0, n = 0;
    for (let i = 0; i < data.length; i += 4) {
      // Rec. 601 luma
      sum += 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
      n += 1;
    }
    return n ? sum / n : 0;
  } catch {
    return 128; // if canvas is tainted or fails, assume ok
  }
}

/**
 * Take the raw File from an <input>, normalise it into a compressed JPEG
 * File ready to upload. Rejects with a `CAPTURE_ERRORS.*` code on failure.
 */
export async function prepareCaptureImage(file) {
  if (!file) throw new Error(CAPTURE_ERRORS.UNSUPPORTED);
  if (!/^image\/(jpeg|jpg|png|webp|heic|heif)$/i.test(file.type)) {
    // Some phones send image/* for HEIC — accept if extension hints jpg/heic.
    if (!/\.(jpe?g|png|webp|heic|heif)$/i.test(file.name || "")) {
      throw new Error(CAPTURE_ERRORS.UNSUPPORTED);
    }
  }
  if (file.size > MAX_INPUT_BYTES) throw new Error(CAPTURE_ERRORS.TOO_LARGE);

  const img = await _loadImage(file);
  const w = img.naturalWidth || img.width;
  const h = img.naturalHeight || img.height;
  if (Math.min(w, h) < MIN_EDGE) throw new Error(CAPTURE_ERRORS.TOO_SMALL);

  // Downscale if either edge exceeds MAX_EDGE while preserving aspect ratio.
  let tw = w, th = h;
  const longer = Math.max(w, h);
  if (longer > MAX_EDGE) {
    const s = MAX_EDGE / longer;
    tw = Math.round(w * s);
    th = Math.round(h * s);
  }

  const canvas = document.createElement("canvas");
  canvas.width = tw; canvas.height = th;
  const ctx = canvas.getContext("2d");
  // Modern browsers apply EXIF orientation automatically when drawing an
  // HTMLImageElement — no manual rotation needed. See MDN "image-orientation".
  ctx.drawImage(img, 0, 0, tw, th);

  if (_meanLuma(canvas) < MIN_LUMA) throw new Error(CAPTURE_ERRORS.TOO_DARK);

  const blob = await new Promise((res) => canvas.toBlob(res, "image/jpeg", 0.85));
  if (!blob) throw new Error(CAPTURE_ERRORS.DECODE_FAIL);
  return new File([blob], (file.name || "scan").replace(/\.[^.]+$/, "") + ".jpg", {
    type: "image/jpeg",
    lastModified: Date.now(),
  });
}

export function isMobile() {
  if (typeof navigator === "undefined") return false;
  return /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent);
}
